import React, {
  forwardRef,
  useEffect,
  cloneElement,
  isValidElement,
  ReactElement,
  Children,
  useState,
  KeyboardEvent,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import classNames from 'classnames';
import { useMenuContext } from './Menu';
import { MenuItemProps, MenuIcon } from './MenuItem';
import {
  menu_base_css,
  menu_item_base_css,
  getMenuItemStyles,
  getMenuGroupContainerHeight,
  menu_group_container_css,
  menu_group_list_css,
} from './styles';
import { IconArrowChevronDown } from '../icons/generated';
import { Stack } from '../stack';
import { BaseText } from '../text';
import { semantic_colors } from '../../foundation';

export const MenuGroup = forwardRef<HTMLLIElement, MenuItemProps>(
  ({ className, id, label, icon, suffix, children, ...props }, ref) => {
    const {
      activeMenu,
      activeMenuGroup,
      setActiveMenuGroup,
      openMenuGroup,
      setOpenMenuGroup,
      onMenuGroupOpenChange,
      toggleMode,
      dense,
    } = useMenuContext();
    const [mounted, setMounted] = useState(false);
    const selected = activeMenuGroup === id;
    const isExpanded = openMenuGroup === id;
    const validChildrenCount = Children.count(children);
    const childMenuItemId: Array<string | null> =
      // @ts-expect-error: TS2345
      Children.map(children, (child: ReactElement<MenuItemProps>) => isValidElement(child) && child.props.id) ?? [];

    const handleClickMenu = () => {
      setOpenMenuGroup(toggleMode ? (isExpanded ? undefined : id) : id);
      setMounted(false);
    };
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleClickMenu();
      }
    };

    useEffect(() => {
      if (childMenuItemId.includes(activeMenu ?? null)) {
        setActiveMenuGroup(id);
        setOpenMenuGroup(id);
      }
    }, [activeMenu]);

    useEffect(() => {
      setMounted(true);
    }, []);

    return (
      <li role='none' className={classNames('pds-menu-group', className)} {...props} ref={ref}>
        <Stack
          role='menuitem'
          tabIndex={0}
          aria-expanded={isExpanded}
          aria-haspopup={validChildrenCount > 0}
          direction='row'
          css={[menu_item_base_css, getMenuItemStyles(dense)]}
          align='center'
          gap={8}
          onClick={handleClickMenu}
          onKeyDown={handleKeyDown}
        >
          {icon && <MenuIcon selected={selected}>{icon}</MenuIcon>}
          <Stack direction='row' gap={4} mr='auto' align='center'>
            <BaseText
              kind={selected ? 'Body_13_SemiBold' : 'Body_13_Medium'}
              color={selected ? semantic_colors.accent.primary : semantic_colors.content.primary}
            >
              {label}
            </BaseText>
            {suffix}
          </Stack>
          <motion.div
            animate={{
              rotate: isExpanded ? 180 : 0,
            }}
            transition={{ duration: 0.25 }}
          >
            <MenuIcon selected={selected}>
              <IconArrowChevronDown size={12} />
            </MenuIcon>
          </motion.div>
        </Stack>
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={selected && mounted ? 'expand' : 'close'}
              animate='expand'
              exit='close'
              css={menu_group_container_css}
              variants={{
                expand: { height: getMenuGroupContainerHeight(dense, validChildrenCount) },
                close: { height: 0 },
              }}
              transition={{ type: 'easeIn', duration: 0.25 }}
              onAnimationComplete={() => onMenuGroupOpenChange?.(id)}
            >
              <ul role='menu' css={[menu_base_css, menu_group_list_css]}>
                {Children.map(
                  // @ts-expect-error: TS2345
                  children,
                  (child: ReactElement<MenuItemProps>) =>
                    isValidElement(child) &&
                    cloneElement(child, {
                      className: 'pds-menu-group__item',
                      groupId: id,
                    }),
                )}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </li>
    );
  },
);
